import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AuthContext, MessageService } from '@heda/core-hospital-ui';
import { LocalizedName } from '@heda/core-util';
import { fromPipe } from '@heda/core-util/util/pipe.util';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalDocumentService } from 'app/integration/document/services/clinical-document.service';
import { DigiluguReferral, EConsultationReferralResponse, EconResponseStatus, ECON_RESP_STATUS } from 'app/integration/document/services/digilugu-referral';
import { DigiluguReferralService } from 'app/integration/document/services/digilugu-referral.service';
import { EConsultationReferralService } from 'app/integration/document/services/e-consultation-referral.service';
import { CodeSystemConcept } from 'app/mdm/terminology-lib/services/codesystem';
import { PatientIdentifier } from 'app/mpi/lib/patient/services/patient';
import { PatientContext } from 'app/mpi/lib/patient/services/patient-context';
import { Appointment } from 'app/reception/lib/appointment/services/appointment';
import { AppointmentService } from 'app/reception/lib/appointment/services/appointment.service';
import { EncounterService } from 'app/reception/lib/services/encounter.service';
import { Referral } from 'app/reception/lib/services/referral/referral';
import { ReferralService } from 'app/reception/lib/services/referral/referral-service';
import { WaitlistResponse } from 'app/reception/waitlist/services/waitlist-response';
import { WaitlistResponseService } from 'app/reception/waitlist/services/waitlist-response.service';
import { WaitlistService } from 'app/reception/waitlist/services/waitlist.service';
import { Condition } from 'app/treatment/lib/condition/services/condition';
import { ConditionService } from 'app/treatment/lib/condition/services/condition.service';
import { Procedure } from 'app/treatment/lib/procedure/services/procedure';
import { ProcedureService } from 'app/treatment/lib/procedure/services/procedure.service';
import moment from 'moment';
import { concatMap, finalize, switchMap } from 'rxjs/operators';
import { CodeSystemService } from 'app/mdm/terminology-lib/services/codesystem.service';
import { EMPTY, forkJoin } from 'rxjs';

@Component({
  selector: 'hw-e-consultation-response',
  templateUrl: './e-consultation-response.component.html'
})
export class EConsultationResponseComponent implements OnInit {
  public referralLoading: boolean;
  public dlReferral: DigiluguReferral;
  public referral: Referral;
  public form: UntypedFormGroup;
  public saving: boolean;
  private referralId: number;
  private patientId: string;
  private patientIdentifier: PatientIdentifier;
  private waitlistResponseId: number;
  private waitlistPerformerId: string;
  public type: string;
  public patientResponsePolicy: string;
  public allowedPractitioners: string[] = [];
  public appointments: Appointment[];
  public responseCancelled: boolean;
  public showCancellationReason: boolean;
  public responseDecisionLocked: boolean;
  public responseCompleted: boolean;
  public referralModified: Date;
  public hasDecisionConcepts: boolean;

  constructor(
    private eConsultationReferralService: EConsultationReferralService,
    private route: ActivatedRoute,
    private dlReferralService: DigiluguReferralService,
    private referralService: ReferralService,
    private patientContext: PatientContext,
    private fb: UntypedFormBuilder,
    private location: Location,
    private documentService: ClinicalDocumentService,
    private waitlistResponseService: WaitlistResponseService,
    private waitlistService: WaitlistService,
    private procedureService: ProcedureService,
    private conditionService: ConditionService,
    private appointmentService: AppointmentService,
    private translateService: TranslateService,
    private authContext: AuthContext,
    private messageService: MessageService,
    private encounterService: EncounterService,
    private codeSystemService: CodeSystemService
  ) {
    this.form = this.createForm();
    this.form.patchValue({
      policy: [
        { actor: 'patient', policy: 'allow' },
        { actor: 'practitioner', policy: 'allow' },
        { actor: 'representee', policy: 'allow' }
      ]
    });
  }

  public ngOnInit(): void {
    this.form.get('type').valueChanges.subscribe((val) => {
      const comment = this.form.controls['comment'];
      const reason = this.form.controls['cancellationReason'];
      if (val === '01') {
        comment.setValidators([Validators.required]);
      } else {
        comment.clearValidators();
      }

      if (val === '05') {
        this.showCancellationReason = true;
        reason.setValidators([Validators.required]);
      } else {
        this.form.patchValue({ cancellationReason: null });
        this.showCancellationReason = false;
        reason.clearValidators();
      }
      reason.updateValueAndValidity();
      comment.updateValueAndValidity();
    });
    this.form.get('diagnosis').valueChanges.subscribe((val) => {
      const control = this.form.controls['diagnosisRecurrence'];
      if (!val) {
        control.clearValidators();
        this.form.patchValue({ diagnosisRecurrence: null });
      } else {
        control.setValidators([Validators.required]);
      }
      control.updateValueAndValidity();
    });
    this.referralId = Number(this.route.snapshot.paramMap.get('referralId'));
    this.waitlistPerformerId = this.route.snapshot.paramMap.get('waitlistPerformerId');
    if (this.waitlistPerformerId) {
      this.allowedPractitioners.push(this.waitlistPerformerId);
      this.form.patchValue({ author: { id: this.waitlistPerformerId } });
    }
    const currentPractitioner = this.authContext.userInfo.reference.practitioner.id;
    if (currentPractitioner !== this.waitlistPerformerId) {
      this.allowedPractitioners.push(currentPractitioner);
    }
    this.type = this.route.snapshot.paramMap.get('type');
    this.patientIdentifier = fromPipe(this.route.snapshot.paramMap.get('patientIdentifier')) as PatientIdentifier;
    const wri = this.route.snapshot.paramMap.get('waitlistResponseId');
    if (wri) {
      this.waitlistResponseId = Number(wri);
      this.initWaitlistResponseData(this.waitlistResponseId);
    }
    this.initReferral();
  }

  private initWaitlistResponseData(waitlistResponseId: number): void {
    this.waitlistResponseService.load(waitlistResponseId).subscribe((resp) => {
      this.responseCancelled = resp.status === 'cancelled';
      this.responseCompleted = resp.status === ECON_RESP_STATUS.completed;
      if (!this.validateAuthor()) {
        this.responseDecisionLocked = true;
        this.disableControlsForInvalidAuthor();
      }
      this.form.patchValue({
        additionalComment: resp.additionalCommentDraft,
        decision: resp.decision,
        cancellationReason: { code: resp.cancellationReason }
      });
      this.initTypeChange(resp);
      this.initAppointments(resp.waitlistId);
      this.procedureService.loadProcedure(resp.procedureId).subscribe((proc) => {
        this.setProcedureParams(proc);
        this.conditionService
          .search({ link: 'HealthRecord|' + proc.healthRecordId, types: ['clinical'] })
          .subscribe((c) => {
            if (c.data?.length > 0) {
              this.setDiagnosis(c.data[0]);
            }
          });
      });
    });
  }

  private disableControlsForInvalidAuthor() {
    this.form.controls['author'].disable();
    this.form.controls['type'].disable();
    this.form.controls['decision'].disable();
    this.form.controls['comment'].disable();
  }

  private initTypeChange(resp: WaitlistResponse): void {
    this.waitlistService.load(resp.waitlistId).subscribe((waitlist) => {
      if (waitlist.status.code === 'booked') {
        this.form.controls['type'].disable();
      } else if (waitlist.status.code === 'fulfilled') {
        this.encounterService.query({ referralId: waitlist.referral.id }).subscribe((enc) => {
          if (enc.meta.total > 0) {
            this.form.controls['type'].disable();
            this.form.controls['decision'].disable();
          }
        });
      }
      this.referralModified = waitlist.referralModified;
    });
  }

  private initReferral(): void {
    this.referralLoading = true;
    forkJoin([
      this.referralService.load(this.referralId),
      this.codeSystemService.getConceptsAttributeMapped('consultation-decision', 'consultation-service')
    ])
      .pipe(
        concatMap(([referral, conceptsMap]) => {
          this.referral = referral;
          this.patientId = referral.subject.id;
          this.hasDecisionConcepts = !!conceptsMap[referral?.performer?.consultationService?.code];
          return this.patientContext.setPatientById(this.patientId).pipe(switchMap(patient => {
            if (referral.dlReferralDeleted) {
              return EMPTY;
            }
            this.patientIdentifier.country = patient.identifiers.find((i) => i.system === this.patientIdentifier.system)?.country;
            return this.dlReferralService.load(referral.identifier, this.patientIdentifier);
          }));
        }),
        finalize(() => this.referralLoading = false)
      )
      .subscribe((dl) => {
        this.dlReferral = dl;
        this.setPatientPolicy(dl);
      });
  }

  public openDocument(referral: DigiluguReferral): void {
    this.documentService.openDocument('e-visit', referral.identifier, referral.patient.identifier);
  }

  private createForm(): UntypedFormGroup {
    return this.fb.group({
      type: [null, [Validators.required]],
      decision: [null],
      comment: [null],
      additionalComment: [null],
      diagnosis: [null],
      diagnosisText: [null],
      diagnosisRecurrence: [null],
      policy: [null],
      author: [null, [Validators.required]],
      cancellationReason: [null]
    });
  }
  public save(): void {
    if (this.responseDecisionLocked || this.validateAuthor('integration.econ-01.response.save-not-allowed')) {
      this.saveResponse(ECON_RESP_STATUS.draft);
    }
  }

  public confirm(): void {
    if (!this.form.valid) {
      this.messageService.error('core.msg.form.invalid');
      return;
    }
    if (this.responseDecisionLocked || this.validateAuthor('integration.econ-01.response.confirmation-not-allowed')) {
      this.saveResponse(ECON_RESP_STATUS.completed);
    }
  }

  public saveResponse(statusCode: EconResponseStatus): void {
    this.saving = true;
    const value = this.form.getRawValue();
    const referralResponse = new EConsultationReferralResponse();
    referralResponse.author = value.author;
    referralResponse.patientId = this.patientId;
    referralResponse.type = { code: value.type };
    referralResponse.decision = value.decision ? { code: value.decision } : undefined;
    if (value.cancellationReason) {
      referralResponse.cancellationReason = value.cancellationReason;
    }
    referralResponse.comment = value.comment;
    referralResponse.additionalCommentDraft = value.additionalComment;
    referralResponse.referralId = this.referralId;
    referralResponse.policy = value.policy;
    referralResponse.status = { code: statusCode };
    referralResponse.waitlistResponseId = this.waitlistResponseId;

    if (value.diagnosis) {
      const diagName = new LocalizedName();
      diagName['et'] = value.diagnosis.text;
      referralResponse.diagnosis = {
        designationNr: value.diagnosis.diagnosisDesignationNr,
        code: { code: value.diagnosis.code, names: diagName },
        text: value.diagnosisText,
        recurrence: value.diagnosisRecurrence
      };
    }
    this.eConsultationReferralService
      .sendResponse(referralResponse)
      .pipe(finalize(() => this.saving = false))
      .subscribe(() => {
        if (statusCode === ECON_RESP_STATUS.completed) {
          this.location.back();
        }
      });
  }

  public eConsultFilter = (cs: CodeSystemConcept): boolean => {
    return cs.attributes['e-consultation-usage'];
  }

  public decisionFilter = (cs: CodeSystemConcept): boolean => {
    return cs.attributes['consultation-service'] && cs.attributes['consultation-service'] === this.referral?.performer?.consultationService?.code;
  }

  private setProcedureParams(proc: Procedure): void {
    if (proc.notes?.length >= 0) {
      for (const note of proc.notes) {
        if (note.type === 'note') {
          this.form.patchValue({
            comment: note.content,
            type: note.finding
          });
        }
      }
    }
    this.form.patchValue({ policy: proc.policy });
  }

  private setDiagnosis(condition: Condition): void {
    this.form.patchValue({
      diagnosis: {
        code: condition.diagnosis.code,
        diagnosisDesignationNr: condition.version.diagnosis.designationNr,
        text: condition.version.diagnosis.display
      },
      diagnosisText: condition.version.note,
      diagnosisRecurrence: condition.version.recurrence?.code
    });
  }

  private setPatientPolicy(dl: DigiluguReferral): void {
    if (dl.confidentiality) {
      this.patientResponsePolicy = dl.confidentiality?.find((c) => c.actor === 'patient-response')?.policy;
      const policy = this.form.value['policy'];
      const pp = policy.find((p) => p.actor === 'patient');
      if (pp) {
        pp.policy = this.patientResponsePolicy ? this.patientResponsePolicy : 'allow';
      } else {
        policy.push({ actor: 'patient', policy: 'allow' });
      }
      this.form.patchValue({ policy: policy });
    }
  }

  public addAppointmentData(app: Appointment): void {
    const prefix = this.translateService.instant('integration.econ-01.appointment.prefix') + ': ';
    const date = moment(app.valid.lower).format('DD.MM.yyyy HH:mm');
    let comment = this.form.getRawValue().comment;
    comment = comment ? comment + '\n\n' + prefix + date : prefix + date;
    this.form.patchValue({ comment: comment });
  }

  public handleAdditionalComment(): void {
    const existingComment = this.form.getRawValue().comment;
    const addedComment = this.getCommentText(this.form.value['additionalComment']);
    const comment = `${existingComment ? `${existingComment}\n\n${addedComment}` : addedComment}`;
    this.form.patchValue({
      comment: comment,
      additionalComment: null
    });
  }

  public formatAdditionalCommentConfirmTitle = (addToComment: string): string => {
    return `${this.translateService.instant('integration.econ-01.response.add-text-confirm-prefix')} '${this.getCommentText(addToComment)}'?`;
  }

  private getCommentText(add_to_comment: string): string {
    return `${this.translateService.instant('integration.econ-01.response.additional-info')}: ${add_to_comment}`;
  }

  private validateAuthor(errorMsg?: string): boolean {
    if (this.form.value.author.id !== this.authContext.userInfo.reference.practitioner.id) {
      if (errorMsg) {
        this.messageService.error(errorMsg);
      }
      return false;
    }
    return true;
  }

  private initAppointments(waitlistId: number): void {
    this.appointmentService.query({ waitlistId: waitlistId, statuses: 'booked' }).subscribe((result) => {
      this.appointments = result?.data;
    });
  }
}
